import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiFetch, clearAuthToken } from '../api/client';
import OpsLayout from '../components/Layout/OpsLayout';
import './OpsHome.css';
import './OpsNfps.css';
import './OpsConsumerDeletionQueue.css';

const FINALIZE_REASONS = [
  'user_requested_immediate_finalization',
  'fraud_or_security_concern',
  'stuck_pending_state',
  'test_or_duplicate_account_cleanup',
  'other',
];

const SUPPORT_QUEUES = [
  {
    key: 'overview',
    label: 'Overview',
    description: 'Onboarding and Plaid health metrics for active support monitoring.',
  },
  {
    key: 'plaid_health_queue',
    label: 'Plaid Health Queue',
    description: 'Users with bank connection states that need support visibility.',
  },
  {
    key: 'plaid_duplicate_accounts',
    label: 'Duplicate Accounts',
    description: 'Cross-profile Plaid account identity collisions and report-only duplicate findings.',
  },
  {
    key: 'deletion_queue',
    label: 'Deletion Queue',
    description: 'Consumers in account deletion flow requiring support attention.',
  },
];

const HEALTH_STATUS_ORDER = [
  'item_revoked_or_disconnected',
  'partially_revoked_or_account_missing',
  'login_required',
  'consent_expiring',
  'healthy',
  'not_connected',
];

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString('en-US');
}

function formatDuration(value) {
  const ms = Number(value || 0);
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} sec`;
}

function titleize(value) {
  return String(value || '')
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function healthChipClass(status) {
  if (status === 'healthy') return 'good';
  if (status === 'consent_expiring') return 'info';
  if (status === 'not_connected') return '';
  return 'bad';
}

function getTile(overview, key) {
  return (overview?.tiles || []).find((tile) => tile.key === key) || { value: 0, caption: '' };
}

// Copy-vs-source audit for the anonymousId -> consumerId stitch.
// `dropped` and `deduped` are rendered as SEPARATE rows on purpose: an absorbed retry
// is normal and a lost event is not, and merging them gives a number that is never
// zero — which is a number people stop reading. That is how the original duplicate-key
// collision stayed hidden for weeks.
// Durable: derived from stored state, so it reads the same on any instance and after
// any restart. `balanced` depends on these and only these.
const STITCH_DURABLE_ROWS = [
  { key: 'orphan', label: 'Orphan events', expectZero: true, caption: 'Should have been stamped and was not' },
  { key: 'conflict', label: 'Conflicting events', expectZero: true, caption: 'Stamped with an id never bound here' },
  { key: 'bindingLost', label: 'Bindings lost', expectZero: true, caption: 'Profile stitched but no binding row' },
  // Not a failure, so it does not clear `balanced` — but not resolved either. Flagged
  // in its own right so a balanced corpus with unattributed events never reads as
  // "nothing to do"; the repair sweep is what clears it.
  { key: 'ambiguous', label: 'Unresolved attribution', expectZero: false, unresolved: true,
    caption: 'Timestamp unusable — refused, not failed. Cleared by the repair sweep.' },
  { key: 'rebound', label: 'Devices rebound', expectZero: false, caption: 'Device carrying more than one consumer' },
  { key: 'bound', label: 'Devices bound', expectZero: false, caption: 'anonymousIds linked to a consumer' },
  { key: 'unbound', label: 'Devices unbound', expectZero: false, caption: 'Never registered — the funnel top' },
];

// Process-local: resets on restart, counts THIS instance only. Kept in its own group
// with its own heading because a zero here means "nothing since this process started
// on this box", not "nothing ever" — and because folding them into `balanced` would
// let a per-instance observation masquerade as a durable guarantee.
const STITCH_PROCESS_ROWS = [
  { key: 'bindFailed', label: 'Binding writes failed', expectZero: true, caption: 'Login failures are invisible in stored state' },
  { key: 'dropped', label: 'Event writes dropped', expectZero: true, caption: 'Distinct events lost to a key collision' },
  { key: 'deduped', label: 'Retries absorbed', expectZero: false, caption: 'Duplicate sends — normal, not a fault' },
  // NOT expectZero, and deliberately not red: non-zero is the finding, not the fault.
  // A shipped app running ahead of the server's allowlist is the condition this counts,
  // and it is the only way to see which keys the server is discarding right now. The
  // key NAMES are in the API response under processCounters.unknownMetadataKeysByName;
  // this row is the total, so the count is readable from the panel rather than the
  // network tab. Q11 renders the names per key in the modal.
  { key: 'unknownMetadataKeys', label: 'Metadata keys dropped', expectZero: false, caption: 'Client sent keys the allowlist does not accept — see unknownMetadataKeysByName' },
];

function Modal({ open, title, children, onClose }) {
  if (!open) return null;
  return (
    <div className="ops-preview-backdrop" onClick={onClose}>
      <section className="ops-preview-modal ops-deletion-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <header className="ops-preview-header">
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button type="button" className="ops-btn" onClick={onClose}>Close</button>
        </header>
        <div>{children}</div>
      </section>
    </div>
  );
}

export default function OpsConsumerDeletionQueue() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [overview, setOverview] = useState(null);
  const [healthRows, setHealthRows] = useState([]);
  const [healthSearch, setHealthSearch] = useState('');
  const [includeConsentExpiring, setIncludeConsentExpiring] = useState(true);
  const [duplicateRows, setDuplicateRows] = useState([]);
  const [duplicateReportRows, setDuplicateReportRows] = useState([]);
  const [duplicateView, setDuplicateView] = useState('identities');
  const [duplicateConfidence, setDuplicateConfidence] = useState('');
  const [duplicateClassification, setDuplicateClassification] = useState('cross_consumer_duplicate');
  const [duplicateNeedsReauth, setDuplicateNeedsReauth] = useState(false);
  const [duplicateHasFinancialEffects, setDuplicateHasFinancialEffects] = useState(false);
  const [duplicateBackfillStatus, setDuplicateBackfillStatus] = useState('');
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilters, setStatusFilters] = useState(['pending', 'processing']);
  const [selectedRow, setSelectedRow] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [plaidReasonModalOpen, setPlaidReasonModalOpen] = useState(false);
  const [finalizeRow, setFinalizeRow] = useState(null);
  const [reason, setReason] = useState(FINALIZE_REASONS[0]);
  const [note, setNote] = useState('');
  const [finalizeError, setFinalizeError] = useState('');
  const [finalizeSuccess, setFinalizeSuccess] = useState('');
  const [copySuccess, setCopySuccess] = useState('');
  const [opsRole, setOpsRole] = useState('');
  const initialQueue = useMemo(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (SUPPORT_QUEUES.some((queue) => queue.key === tab)) return tab;
    if (location.pathname.includes('/duplicate-accounts')) return 'plaid_duplicate_accounts';
    if (location.pathname.includes('/deletion-queue')) return 'deletion_queue';
    return 'overview';
  }, [location.pathname, location.search]);
  const [selectedQueue, setSelectedQueue] = useState(initialQueue);

  const canFinalize = opsRole === 'super_admin' || opsRole === 'ops_manager';

  async function loadProfile() {
    const me = await apiFetch('/ops/me');
    return String(me?.internalOpsMembership?.role || '');
  }

  async function fetchOverview() {
    return apiFetch('/ops/admin/consumers/support-overview');
  }

  async function fetchPlaidHealthQueue(activeSearch = healthSearch, activeIncludeConsent = includeConsentExpiring) {
    const params = new URLSearchParams();
    params.set('limit', '250');
    params.set('includeConsentExpiring', activeIncludeConsent ? 'true' : 'false');
    if (activeSearch.trim()) params.set('search', activeSearch.trim());
    return apiFetch(`/ops/admin/consumers/plaid-health-queue?${params.toString()}`);
  }

  async function fetchDuplicateIdentities({
    confidence = duplicateConfidence,
    classification = duplicateClassification,
    needsReauth = duplicateNeedsReauth,
    hasFinancialEffects = duplicateHasFinancialEffects,
  } = {}) {
    const params = new URLSearchParams();
    params.set('limit', '250');
    if (confidence) params.set('confidence', confidence);
    if (classification) params.set('classification', classification);
    if (needsReauth) params.set('needsReauth', 'true');
    if (hasFinancialEffects) params.set('hasFinancialEffects', 'true');
    return apiFetch(`/ops/admin/consumers/plaid-duplicate-identities?${params.toString()}`);
  }

  async function fetchDuplicateReport({
    confidence = duplicateConfidence,
    classification = duplicateClassification,
    needsReauth = duplicateNeedsReauth,
    hasFinancialEffects = duplicateHasFinancialEffects,
  } = {}) {
    const params = new URLSearchParams();
    params.set('limit', '250');
    if (confidence) params.set('confidence', confidence);
    if (classification) params.set('classification', classification);
    if (needsReauth) params.set('needsReauth', 'true');
    if (hasFinancialEffects) params.set('hasFinancialEffects', 'true');
    return apiFetch(`/ops/admin/consumers/plaid-duplicate-report?${params.toString()}`);
  }

  async function fetchDeletionQueue(activeStatuses = statusFilters, activeSearch = search) {
    const params = new URLSearchParams();
    params.set('statuses', activeStatuses.join(','));
    if (activeSearch.trim()) params.set('search', activeSearch.trim());
    params.set('limit', '250');
    return apiFetch(`/ops/admin/consumers/deletion-queue?${params.toString()}`);
  }

  function handleAuthFailure(err) {
    if (err?.status === 401 || err?.status === 403) {
      clearAuthToken();
      navigate('/login', { replace: true });
      return true;
    }
    return false;
  }

  async function loadOverview() {
    setLoading(true);
    setError('');
    try {
      const data = await fetchOverview();
      setOverview(data || null);
    } catch (err) {
      if (handleAuthFailure(err)) return;
      setOverview(null);
      setError(err?.data?.error || err?.message || 'Failed to load consumer support overview');
    } finally {
      setLoading(false);
    }
  }

  async function loadPlaidHealthQueue(activeSearch = healthSearch, activeIncludeConsent = includeConsentExpiring) {
    setLoading(true);
    setError('');
    try {
      const data = await fetchPlaidHealthQueue(activeSearch, activeIncludeConsent);
      setHealthRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (err) {
      if (handleAuthFailure(err)) return;
      setHealthRows([]);
      setError(err?.data?.error || err?.message || 'Failed to load Plaid health queue');
    } finally {
      setLoading(false);
    }
  }

  async function loadDuplicateAccounts(filters = {}) {
    setLoading(true);
    setError('');
    try {
      const [identityData, reportData] = await Promise.all([
        fetchDuplicateIdentities(filters),
        fetchDuplicateReport(filters),
      ]);
      setDuplicateRows(Array.isArray(identityData?.rows) ? identityData.rows : []);
      setDuplicateReportRows(Array.isArray(reportData?.rows) ? reportData.rows : []);
    } catch (err) {
      if (handleAuthFailure(err)) return;
      setDuplicateRows([]);
      setDuplicateReportRows([]);
      setError(err?.data?.error || err?.message || 'Failed to load duplicate Plaid account queue');
    } finally {
      setLoading(false);
    }
  }

  async function loadDeletionQueue(activeStatuses = statusFilters, activeSearch = search) {
    setLoading(true);
    setError('');
    try {
      const data = await fetchDeletionQueue(activeStatuses, activeSearch);
      setRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (err) {
      if (handleAuthFailure(err)) return;
      setRows([]);
      setError(err?.data?.error || err?.message || 'Failed to load consumer deletion queue');
    } finally {
      setLoading(false);
    }
  }

  async function refreshActiveQueue() {
    if (selectedQueue === 'overview') {
      await loadOverview();
    } else if (selectedQueue === 'plaid_health_queue') {
      await loadPlaidHealthQueue(healthSearch, includeConsentExpiring);
    } else if (selectedQueue === 'plaid_duplicate_accounts') {
      await loadDuplicateAccounts();
    } else {
      await loadDeletionQueue(statusFilters, search);
    }
  }

  useEffect(() => {
    let cancelled = false;
    async function init() {
      setLoading(true);
      setError('');
      try {
        const role = await loadProfile();
        const [overviewData, plaidHealthData, duplicateIdentityData, duplicateReportData, deletionData] = await Promise.all([
          fetchOverview(),
          fetchPlaidHealthQueue('', true),
          fetchDuplicateIdentities({ confidence: '', classification: 'cross_consumer_duplicate', needsReauth: false, hasFinancialEffects: false }),
          fetchDuplicateReport({ confidence: '', classification: 'cross_consumer_duplicate', needsReauth: false, hasFinancialEffects: false }),
          fetchDeletionQueue(['pending', 'processing'], ''),
        ]);
        if (cancelled) return;
        setOpsRole(role);
        setOverview(overviewData || null);
        setHealthRows(Array.isArray(plaidHealthData?.rows) ? plaidHealthData.rows : []);
        setDuplicateRows(Array.isArray(duplicateIdentityData?.rows) ? duplicateIdentityData.rows : []);
        setDuplicateReportRows(Array.isArray(duplicateReportData?.rows) ? duplicateReportData.rows : []);
        setRows(Array.isArray(deletionData?.rows) ? deletionData.rows : []);
      } catch (err) {
        if (!cancelled && !handleAuthFailure(err)) {
          setError(err?.data?.error || err?.message || 'Failed to load consumer support');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    init();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    setSelectedQueue(initialQueue);
  }, [initialQueue]);

  async function openDetail(row) {
    setSelectedRow(row);
    setDetail(null);
    setDetailLoading(true);
    try {
      const data = await apiFetch(`/ops/admin/consumers/${row.consumerId}/delete-status`);
      setDetail(data || null);
    } catch (err) {
      setDetail({ error: err?.data?.error || err?.message || 'Failed to load deletion detail' });
    } finally {
      setDetailLoading(false);
    }
  }

  function toggleStatus(value) {
    const next = statusFilters.includes(value)
      ? statusFilters.filter((s) => s !== value)
      : [...statusFilters, value];
    const fixed = next.length ? next : ['pending'];
    setStatusFilters(fixed);
  }

  async function handleApplyFilters() {
    await loadDeletionQueue(statusFilters, search);
  }

  async function handleApplyHealthFilters() {
    await loadPlaidHealthQueue(healthSearch, includeConsentExpiring);
  }

  async function handleApplyDuplicateFilters() {
    await loadDuplicateAccounts({
      confidence: duplicateConfidence,
      classification: duplicateClassification,
      needsReauth: duplicateNeedsReauth,
      hasFinancialEffects: duplicateHasFinancialEffects,
    });
  }

  async function handleBackfillDuplicateIdentities() {
    setSubmitting(true);
    setDuplicateBackfillStatus('');
    setError('');
    try {
      const data = await apiFetch('/ops/admin/consumers/plaid-duplicate-identities/backfill', {
        method: 'POST',
        body: { markSuspectedDuplicates: true },
      });
      const summary = data?.summary || {};
      setDuplicateBackfillStatus(
        `Backfill complete: ${formatNumber(summary.plaidItemsScanned)} items scanned, ${formatNumber(summary.duplicateGroupsFound)} duplicate groups found.`
      );
      await loadDuplicateAccounts();
    } catch (err) {
      if (handleAuthFailure(err)) return;
      setError(err?.data?.error || err?.message || 'Failed to run duplicate identity backfill');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCopy(value, label = 'Value') {
    const text = String(value || '').trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(`${label} copied.`);
      window.setTimeout(() => setCopySuccess(''), 2500);
    } catch (err) {
      setCopySuccess('Copy failed. Select the value manually.');
      window.setTimeout(() => setCopySuccess(''), 2500);
    }
  }

  async function handleFinalizeSubmit() {
    if (!finalizeRow) return;
    if (!FINALIZE_REASONS.includes(reason)) {
      setFinalizeError('Select a reason code.');
      return;
    }
    if (reason === 'other' && !note.trim()) {
      setFinalizeError('Note is required when reason is Other.');
      return;
    }

    setSubmitting(true);
    setFinalizeError('');
    setFinalizeSuccess('');
    try {
      await apiFetch(`/ops/admin/consumers/${finalizeRow.consumerId}/finalize-deletion`, {
        method: 'POST',
        body: {
          reason,
          note: note.trim() || undefined,
        },
      });
      setFinalizeSuccess('Deletion finalized successfully.');
      await loadDeletionQueue(statusFilters, search);
      if (selectedRow?.consumerId === finalizeRow.consumerId) {
        await openDetail(finalizeRow);
      }
    } catch (err) {
      setFinalizeError(err?.data?.error || err?.message || 'Failed to finalize deletion');
    } finally {
      setSubmitting(false);
    }
  }

  const summary = useMemo(() => {
    const out = { pending: 0, processing: 0 };
    rows.forEach((row) => {
      if (row.status === 'pending') out.pending += 1;
      if (row.status === 'processing') out.processing += 1;
    });
    return out;
  }, [rows]);

  const healthSummary = useMemo(() => {
    return healthRows.reduce((out, row) => {
      const status = row.healthStatus || 'unknown';
      out[status] = (out[status] || 0) + 1;
      return out;
    }, {});
  }, [healthRows]);

  const queueCards = useMemo(() => {
    return SUPPORT_QUEUES.map((queue) => {
      if (queue.key === 'overview') {
        const profiles = getTile(overview, 'profiles_created').value;
        const supportQueues = overview?.supportQueues || {};
        const needsAttention =
          Number(supportQueues.plaidHealthQueueCount || 0) +
          Number(supportQueues.deletionQueueCount || 0);
        return {
          ...queue,
          needsAttention,
          metricCaption: `${formatNumber(profiles)} profiles monitored`,
        };
      }
      if (queue.key === 'plaid_health_queue') {
        const repair = Number(healthSummary.login_required || 0)
          + Number(healthSummary.partially_revoked_or_account_missing || 0)
          + Number(healthSummary.item_revoked_or_disconnected || 0);
        const consent = Number(healthSummary.consent_expiring || 0);
        return {
          ...queue,
          needsAttention: healthRows.length,
          metricCaption: `${formatNumber(repair)} repair • ${formatNumber(consent)} consent`,
        };
      }
      if (queue.key === 'deletion_queue') {
        return {
          ...queue,
          needsAttention: summary.pending + summary.processing,
          metricCaption: `${summary.pending} pending • ${summary.processing} processing`,
        };
      }
      if (queue.key === 'plaid_duplicate_accounts') {
        const needsAttention = duplicateRows.length + duplicateReportRows.length;
        const strong = [...duplicateRows, ...duplicateReportRows]
          .filter((row) => (row.identityConfidence || row.matchConfidence) === 'strong').length;
        const financial = [...duplicateRows, ...duplicateReportRows]
          .filter((row) => Number(row.relatedPostedFinancialTransactions || 0) > 0).length;
        return {
          ...queue,
          needsAttention,
          metricCaption: `${formatNumber(strong)} strong • ${formatNumber(financial)} with effects`,
        };
      }
      return {
        ...queue,
        needsAttention: 0,
        metricCaption: 'No data',
      };
    });
  }, [duplicateReportRows, duplicateRows, healthRows.length, healthSummary, overview, summary.pending, summary.processing]);

  const orderedHealthSnapshot = useMemo(() => {
    const statuses = overview?.healthSnapshot?.statuses || [];
    return [...statuses].sort((a, b) => {
      const ai = HEALTH_STATUS_ORDER.indexOf(a.status);
      const bi = HEALTH_STATUS_ORDER.indexOf(b.status);
      return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
    });
  }, [overview]);

  const duplicateDisplayRows = useMemo(() => {
    return duplicateView === 'report' ? duplicateReportRows : duplicateRows;
  }, [duplicateReportRows, duplicateRows, duplicateView]);

  function formatConsumerLabel(consumer) {
    if (!consumer) return '—';
    return consumer.username || consumer.email || consumer._id || '—';
  }

  function renderConsumerBadges(type = {}) {
    const badges = Array.isArray(type?.badges) ? type.badges : [];
    return badges.map((badge) => (
      <span key={badge} className={`ops-chip ${badge === 'Production' ? '' : 'info'}`}>{badge}</span>
    ));
  }

  function chipClassForAction(action) {
    if (action === 'blocked_automatically' || action === 'manual_remediation_needed') return 'bad';
    if (action === 'needs_reauth_follow_up' || action === 'review') return 'info';
    return '';
  }

  return (
    <OpsLayout
      title="Consumer Support"
      subtitle="Track onboarding completion, Plaid connection health, and deletion support queues from one place."
      headerActions={<button type="button" className="ops-btn" onClick={refreshActiveQueue} disabled={loading}>Refresh</button>}
    >
      <section className="ops-panel">
        <h2 className="ops-support-queue-title">Support Queues</h2>
        <div className="ops-support-queue-grid">
          {queueCards.map((queue) => {
            const isActive = selectedQueue === queue.key;
            return (
              <button
                key={queue.key}
                type="button"
                className={`ops-support-queue-card ${isActive ? 'active' : ''}`}
                onClick={() => {
                  setSelectedQueue(queue.key);
                  setError('');
                }}
              >
                <div className="ops-support-queue-label">{queue.label}</div>
                <div className="ops-support-queue-value">{formatNumber(queue.needsAttention)}</div>
                <div className="ops-support-queue-caption">{queue.metricCaption}</div>
                <div className="ops-support-queue-description">{queue.description}</div>
              </button>
            );
          })}
        </div>
      </section>

      {error ? <div className="ops-message error">{error}</div> : null}
      {copySuccess ? <div className="ops-message">{copySuccess}</div> : null}

      {selectedQueue === 'overview' ? (
        <>
          <section className="ops-support-metric-grid">
            {(overview?.tiles || []).map((tile) => (
              <article key={tile.key} className="ops-card ops-support-metric-card">
                <div className="ops-card-title">{tile.label}</div>
                <div className="ops-card-value">{loading && !overview ? '—' : formatNumber(tile.value)}</div>
                {tile.caption ? <div className="ops-card-caption">{tile.caption}</div> : null}
              </article>
            ))}
            {loading && !overview ? (
              <article className="ops-card ops-support-metric-card">
                <div className="ops-card-title">Loading</div>
                <div className="ops-card-value">—</div>
                <div className="ops-card-caption">Fetching overview metrics</div>
              </article>
            ) : null}
          </section>

          <section className="ops-support-overview-panels">
            <article className="ops-panel ops-support-compact-panel">
              <div className="ops-support-panel-heading">
                <h2>Registration funnel</h2>
                <button
                  type="button"
                  className="ops-btn"
                  onClick={() => setPlaidReasonModalOpen(true)}
                  disabled={!overview?.plaidIncompleteReasons?.total}
                >
                  Plaid reasons
                </button>
              </div>
              <div className="ops-support-panel-list">
                {(overview?.registrationFunnel || []).map((step) => (
                  <div key={step.key} className="ops-support-panel-row">
                    <div>
                      <div className="ops-support-panel-label">{step.label}</div>
                      <div className="ops-support-panel-caption">
                        {step.key === 'profiles_created'
                          ? `${step.percentOfProfiles || 0}% of profiles • ${formatNumber(step.cumulativeCount)} total`
                          : `${step.percentOfProfiles || 0}% currently • ${formatNumber(step.cumulativeCount)} total reached`}
                      </div>
                    </div>
                    <strong>{formatNumber(step.count)}</strong>
                  </div>
                ))}
                {!loading && !overview?.registrationFunnel?.length ? (
                  <div className="ops-support-empty">No funnel data available.</div>
                ) : null}
              </div>
            </article>

            <article className="ops-panel ops-support-compact-panel">
              <div className="ops-support-panel-heading">
                <h2>Funnel identity stitch</h2>
                <span className="ops-chip">
                  {(() => {
                    const stitch = overview?.registrationStitch;
                    if (!stitch) return '—';
                    if (!stitch.balanced) return 'Check';
                    // Balanced but not finished: say so rather than showing a clean chip
                    // over events that belong to nobody.
                    if (stitch.hasUnresolvedAttribution) {
                      return `Balanced · ${formatNumber(stitch.unresolvedAttribution)} unresolved`;
                    }
                    return 'Balanced';
                  })()}
                </span>
              </div>
              <div className="ops-support-panel-list">
                {(overview?.registrationStitch ? STITCH_DURABLE_ROWS : []).map((row) => {
                  const value = Number(overview.registrationStitch[row.key] || 0);
                  const bad = row.expectZero && value > 0;
                  const unresolved = row.unresolved && value > 0;
                  return (
                    <div key={row.key} className="ops-support-panel-row">
                      <div>
                        <div className="ops-support-panel-label">{row.label}</div>
                        <div className="ops-support-panel-caption">
                          {row.expectZero ? 'Expected 0 — ' : ''}
                          {row.caption}
                        </div>
                      </div>
                      <strong
                        style={
                          bad ? { color: '#b42318' } : unresolved ? { color: '#b54708' } : undefined
                        }
                      >
                        {formatNumber(value)}
                      </strong>
                    </div>
                  );
                })}
                {!loading && !overview?.registrationStitch ? (
                  <div className="ops-support-empty">No stitch data available.</div>
                ) : null}
              </div>

              {overview?.registrationStitch?.processCounters ? (
                <>
                  <div className="ops-support-panel-heading" style={{ marginTop: 16 }}>
                    <h2 style={{ fontSize: '0.95rem' }}>Since this process started</h2>
                    <span className="ops-chip">
                      {formatDateTime(overview.registrationStitch.processCounters.startedAt)}
                    </span>
                  </div>
                  <div className="ops-support-panel-caption" style={{ marginBottom: 8 }}>
                    Per-instance and reset on restart — a zero here is not proof that nothing
                    happened, only that nothing happened on this server since it started.
                    Durable reconciliation cannot prove it either: an event that never reached
                    the database leaves no row to audit.
                  </div>
                  <div className="ops-support-panel-list">
                    {STITCH_PROCESS_ROWS.map((row) => {
                      const value = Number(overview.registrationStitch.processCounters[row.key] || 0);
                      const bad = row.expectZero && value > 0;
                      return (
                        <div key={row.key} className="ops-support-panel-row">
                          <div>
                            <div className="ops-support-panel-label">{row.label}</div>
                            <div className="ops-support-panel-caption">
                              {row.expectZero ? 'Expected 0 — ' : ''}
                              {row.caption}
                            </div>
                          </div>
                          <strong style={bad ? { color: '#b42318' } : undefined}>{formatNumber(value)}</strong>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : null}
            </article>

            {overview?.consistencyChecks ? (
              <article className="ops-panel ops-support-compact-panel">
                <div className="ops-support-panel-heading">
                  <h2>Consistency checks</h2>
                  <span className={`ops-chip ${overview.consistencyChecks.attention ? 'bad' : 'good'}`}>
                    {overview.consistencyChecks.attention
                      ? `${overview.consistencyChecks.attention} need attention`
                      : 'All clean'}
                  </span>
                </div>
                <div className="ops-support-panel-caption" style={{ marginBottom: 8 }}>
                  Standing checks for fixed defects, run daily and expected 0. A check that has not
                  run in {overview.consistencyChecks.staleAfterHours}h reads stale, not clean.
                </div>
                <div className="ops-support-panel-list">
                  {(overview.consistencyChecks.checks || []).map((check) => (
                    <div key={check.key} className="ops-support-panel-row">
                      <div>
                        <div className="ops-support-panel-label">{check.defect} · {check.label}</div>
                        <div className="ops-support-panel-caption">
                          {check.error
                            ? `Failed to run: ${check.error}`
                            : check.lastRunAt
                              ? `Last run ${formatDateTime(check.lastRunAt)}${check.stale ? ' — stale' : ''}`
                              : 'Never run'}
                          {check.count > 0 && check.sample?.length
                            ? ` · e.g. ${check.sample.slice(0, 3).map((s) => Object.values(s)[0]).join(', ')}`
                            : ''}
                        </div>
                      </div>
                      <strong style={check.clean ? undefined : { color: '#b42318' }}>
                        {check.count == null ? '—' : formatNumber(check.count)}
                      </strong>
                    </div>
                  ))}
                </div>
              </article>
            ) : null}

            {overview?.messageEngine ? (
              <article className="ops-panel ops-support-compact-panel">
                <div className="ops-support-panel-heading">
                  <h2>Message engine</h2>
                  <span className="ops-chip">
                    Since {formatDateTime(overview.messageEngine.startedAt)}
                  </span>
                </div>
                <div className="ops-support-panel-caption" style={{ marginBottom: 8 }}>
                  Home-feed message failures that were absorbed so they could not affect the
                  transaction or request that caused them. Per-instance and reset on restart.
                </div>
                <div className="ops-support-panel-list">
                  <div className="ops-support-panel-row">
                    <div>
                      <div className="ops-support-panel-label">Events failed</div>
                      <div className="ops-support-panel-caption">
                        Expected 0 —{' '}
                        {overview.messageEngine.lastFailure
                          ? `last: ${overview.messageEngine.lastFailure.eventType} · ${overview.messageEngine.lastFailure.message}`
                          : 'no failures on this instance'}
                      </div>
                    </div>
                    <strong style={overview.messageEngine.failed > 0 ? { color: '#b42318' } : undefined}>
                      {formatNumber(overview.messageEngine.failed || 0)}
                    </strong>
                  </div>
                  {Object.entries(overview.messageEngine.failedByEvent || {}).map(([eventType, count]) => (
                    <div key={eventType} className="ops-support-panel-row">
                      <div className="ops-support-panel-caption">{eventType}</div>
                      <strong>{formatNumber(count)}</strong>
                    </div>
                  ))}
                  {overview.transactionSideEffects ? (
                    <>
                      <div className="ops-support-panel-row">
                        <div>
                          <div className="ops-support-panel-label">Transaction side effects failed</div>
                          <div className="ops-support-panel-caption">
                            Expected 0 — catalog enrichment and contribution stats, absorbed so a batch
                            continues.{' '}
                            {overview.transactionSideEffects.lastFailure
                              ? `Last: ${overview.transactionSideEffects.lastFailure.name} · ${overview.transactionSideEffects.lastFailure.message}`
                              : ''}
                          </div>
                        </div>
                        <strong style={overview.transactionSideEffects.failed > 0 ? { color: '#b42318' } : undefined}>
                          {formatNumber(overview.transactionSideEffects.failed || 0)}
                        </strong>
                      </div>
                      {Object.entries(overview.transactionSideEffects.failedByName || {}).map(([name, count]) => (
                        <div key={name} className="ops-support-panel-row">
                          <div className="ops-support-panel-caption">{name}</div>
                          <strong>{formatNumber(count)}</strong>
                        </div>
                      ))}
                    </>
                  ) : null}
                </div>
              </article>
            ) : null}

            <article className="ops-panel ops-support-compact-panel">
              <div className="ops-support-panel-heading">
                <h2>Current health snapshot</h2>
                <span className="ops-chip">Plaid</span>
              </div>
              <div className="ops-support-panel-list">
                {orderedHealthSnapshot.map((entry) => (
                  <div key={entry.status} className="ops-support-panel-row">
                    <div>
                      <div className="ops-support-panel-label">
                        <span className={`ops-support-status-dot ${healthChipClass(entry.status)}`} />
                        {titleize(entry.status)}
                      </div>
                      <div className="ops-support-panel-caption">
                        {entry.status === 'not_connected'
                          ? `${entry.percentOfProfiles || 0}% of profiles`
                          : `${entry.percentOfConnected || 0}% of connected`}
                      </div>
                    </div>
                    <strong>{formatNumber(entry.count)}</strong>
                  </div>
                ))}
                {!loading && !orderedHealthSnapshot.length ? (
                  <div className="ops-support-empty">No health data available.</div>
                ) : null}
              </div>
            </article>

            <article className="ops-panel ops-support-compact-panel">
              <div className="ops-support-panel-heading">
                <h2>Scheduler status</h2>
                <span className={`ops-chip ${overview?.plaidHealthScheduler?.enabled ? 'good' : ''}`}>
                  {overview?.plaidHealthScheduler?.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="ops-support-panel-list">
                <div className="ops-support-panel-row">
                  <div>
                    <div className="ops-support-panel-label">Instance</div>
                    <div className="ops-support-panel-caption">Single-runner beta assumption</div>
                  </div>
                  <strong className="ops-support-compact-value">{overview?.plaidHealthScheduler?.instanceId || '—'}</strong>
                </div>
                <div className="ops-support-panel-row">
                  <div>
                    <div className="ops-support-panel-label">Last run started</div>
                    <div className="ops-support-panel-caption">{formatDateTime(overview?.plaidHealthScheduler?.lastRunStartedAt)}</div>
                  </div>
                  <strong>{overview?.plaidHealthScheduler?.running ? 'Running' : '—'}</strong>
                </div>
                <div className="ops-support-panel-row">
                  <div>
                    <div className="ops-support-panel-label">Last run completed</div>
                    <div className="ops-support-panel-caption">{formatDateTime(overview?.plaidHealthScheduler?.lastRunCompletedAt)}</div>
                  </div>
                  <strong>{formatDuration(overview?.plaidHealthScheduler?.lastRunDurationMs)}</strong>
                </div>
                <div className="ops-support-panel-row">
                  <div>
                    <div className="ops-support-panel-label">Last run failed</div>
                    <div className="ops-support-panel-caption">{formatDateTime(overview?.plaidHealthScheduler?.lastRunFailedAt)}</div>
                  </div>
                  <strong>{overview?.plaidHealthScheduler?.lastRunError || '—'}</strong>
                </div>
                <div className="ops-support-panel-row">
                  <div>
                    <div className="ops-support-panel-label">Evaluations processed</div>
                    <div className="ops-support-panel-caption">Evaluator errors: {formatNumber(overview?.plaidHealthScheduler?.lastEvaluatorErrors)}</div>
                  </div>
                  <strong>{formatNumber(overview?.plaidHealthScheduler?.lastEvaluationsProcessed)}</strong>
                </div>
                <div className="ops-support-note">
                  Beta assumes one scheduler runner. Multi-instance coordination is not implemented.
                </div>
              </div>
            </article>
          </section>

          <Modal
            open={plaidReasonModalOpen}
            title="Plaid Incomplete Reasons"
            onClose={() => setPlaidReasonModalOpen(false)}
          >
            <div className="ops-card-caption" style={{ marginBottom: 16 }}>
              Current consumers who reached Plaid but have not connected an account:
              {' '}
              {formatNumber(overview?.plaidIncompleteReasons?.total || 0)}
            </div>
            <div style={{ display: 'grid', gap: 12 }}>
              {(overview?.plaidIncompleteReasons?.reasons || []).map((reason) => (
                <section key={reason.key} className="ops-panel" style={{ margin: 0 }}>
                  <div className="ops-support-panel-heading">
                    <h3 style={{ margin: 0 }}>{reason.label}</h3>
                    <span className="ops-chip">{formatNumber(reason.count)}</span>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="ops-nfp-table">
                      <thead>
                        <tr>
                          <th>Consumer</th>
                          <th>Status</th>
                          <th>Institution</th>
                          <th>Error</th>
                          <th>Exit</th>
                          <th>Updated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(reason.samples || []).map((sample) => (
                          <tr key={sample.consumerId}>
                            <td>
                              <div>{sample.username || sample.consumerId}</div>
                              <div className="ops-card-caption">{sample.email || '—'}</div>
                            </td>
                            <td><span className="ops-chip">{titleize(sample.status || 'unknown')}</span></td>
                            <td>
                              <div>{sample.institutionName || '—'}</div>
                              {sample.institutionId ? <div className="ops-card-caption">{sample.institutionId}</div> : null}
                            </td>
                            <td>
                              <div>{sample.errorCode || sample.errorType || '—'}</div>
                              {sample.errorMessage ? <div className="ops-card-caption">{sample.errorMessage}</div> : null}
                            </td>
                            <td>{sample.exitStatus || '—'}</td>
                            <td>{formatDateTime(sample.updatedAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              ))}
              {!overview?.plaidIncompleteReasons?.reasons?.length ? (
                <div className="ops-support-empty">No incomplete Plaid registrations in the current funnel.</div>
              ) : null}
            </div>
          </Modal>
        </>
      ) : null}

      {selectedQueue === 'plaid_health_queue' ? (
        <>
          <section className="ops-panel ops-deletion-controls">
            <div className="ops-deletion-summary">
              <span className="ops-chip">Module: Plaid Health Queue</span>
              <span className="ops-chip bad">Repair: {formatNumber((healthSummary.login_required || 0) + (healthSummary.partially_revoked_or_account_missing || 0) + (healthSummary.item_revoked_or_disconnected || 0))}</span>
              <span className="ops-chip info">Consent: {formatNumber(healthSummary.consent_expiring || 0)}</span>
              <span className="ops-chip">Role: {opsRole || 'unknown'}</span>
            </div>
            <div className="ops-deletion-filter-row">
              <label className="ops-deletion-check">
                <input
                  type="checkbox"
                  checked={includeConsentExpiring}
                  onChange={() => setIncludeConsentExpiring((value) => !value)}
                />
                Include consent expiring
              </label>
              <input
                className="ops-deletion-search"
                value={healthSearch}
                onChange={(e) => setHealthSearch(e.target.value)}
                placeholder="Search by email, username, consumer id, item, or reason"
              />
              <button type="button" className="ops-btn primary" onClick={handleApplyHealthFilters} disabled={loading}>Apply</button>
            </div>
          </section>

          <section className="ops-panel" style={{ marginTop: 12 }}>
            <div className="ops-nfp-table-wrap ops-support-table-wrap">
              <table className="ops-nfp-table ops-support-health-table">
                <thead>
                  <tr>
                    <th>Consumer</th>
                    <th>Email</th>
                    <th>Health Status</th>
                    <th>Reason</th>
                    <th>Affected Item</th>
                    <th>Recovery Mode</th>
                    <th>Last Healthy At</th>
                    <th>Last Sync Success</th>
                    <th>Banner Active</th>
                    <th>Dismissed</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={11}>Loading Plaid health queue…</td></tr>
                  ) : healthRows.length === 0 ? (
                    <tr><td colSpan={11}>No consumers in actionable Plaid health states.</td></tr>
                  ) : healthRows.map((row) => (
                    <tr key={`${row.consumerId}-${row.issueKey || row.healthStatus}`}>
                      <td>
                        <div>{row.consumer || row.consumerId}</div>
                        <div className="ops-support-muted">{row.consumerId}</div>
                      </td>
                      <td>{row.email || '—'}</td>
                      <td><span className={`ops-chip ${healthChipClass(row.healthStatus)}`}>{titleize(row.healthStatus)}</span></td>
                      <td>{row.reason ? titleize(row.reason) : '—'}</td>
                      <td><span className="ops-support-monospace">{row.affectedItem || '—'}</span></td>
                      <td><span className="ops-chip">{titleize(row.recoveryMode)}</span></td>
                      <td>{formatDateTime(row.lastHealthyAt)}</td>
                      <td>{formatDateTime(row.lastSyncSuccess)}</td>
                      <td><span className={`ops-chip ${row.bannerActive ? 'bad' : 'good'}`}>{row.bannerActive ? 'Yes' : 'No'}</span></td>
                      <td><span className={`ops-chip ${row.dismissed ? 'info' : ''}`}>{row.dismissed ? 'Yes' : 'No'}</span></td>
                      <td>
                        <div className="ops-deletion-actions">
                          <button type="button" className="ops-btn" onClick={() => handleCopy(row.consumerId, 'Consumer ID')}>Copy ID</button>
                          {row.email ? (
                            <a className="ops-btn" href={`mailto:${row.email}`}>Email</a>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : null}

      {selectedQueue === 'plaid_duplicate_accounts' ? (
        <>
          <section className="ops-panel ops-deletion-controls">
            <div className="ops-deletion-summary">
              <span className="ops-chip">Module: Duplicate Accounts</span>
              <span className="ops-chip">Blocked/attempts: {formatNumber(duplicateRows.length)}</span>
              <span className="ops-chip info">Report groups: {formatNumber(duplicateReportRows.length)}</span>
              <span className="ops-chip">Role: {opsRole || 'unknown'}</span>
            </div>
            <div className="ops-deletion-filter-row">
              <label className="ops-deletion-check">
                <input
                  type="radio"
                  name="duplicateView"
                  checked={duplicateView === 'identities'}
                  onChange={() => setDuplicateView('identities')}
                />
                Block/report events
              </label>
              <label className="ops-deletion-check">
                <input
                  type="radio"
                  name="duplicateView"
                  checked={duplicateView === 'report'}
                  onChange={() => setDuplicateView('report')}
                />
                Existing duplicate report
              </label>
              <select
                className="ops-deletion-search"
                value={duplicateConfidence}
                onChange={(e) => setDuplicateConfidence(e.target.value)}
                style={{ maxWidth: 180 }}
              >
                <option value="">All confidence</option>
                <option value="strong">Strong</option>
                <option value="probable">Probable</option>
              </select>
              <select
                className="ops-deletion-search"
                value={duplicateClassification}
                onChange={(e) => setDuplicateClassification(e.target.value)}
                style={{ maxWidth: 240 }}
              >
                <option value="">All duplicate types</option>
                <option value="cross_consumer_duplicate">Cross-consumer duplicate</option>
                <option value="same_consumer_duplicate">Same consumer duplicate</option>
                <option value="test_internal_duplicate">Test/internal duplicate</option>
                <option value="production_duplicate">Production duplicate</option>
              </select>
              <label className="ops-deletion-check">
                <input
                  type="checkbox"
                  checked={duplicateNeedsReauth}
                  onChange={() => setDuplicateNeedsReauth((value) => !value)}
                />
                Needs reauth
              </label>
              <label className="ops-deletion-check">
                <input
                  type="checkbox"
                  checked={duplicateHasFinancialEffects}
                  onChange={() => setDuplicateHasFinancialEffects((value) => !value)}
                />
                Has effects
              </label>
              <button type="button" className="ops-btn primary" onClick={handleApplyDuplicateFilters} disabled={loading}>Apply</button>
              <button type="button" className="ops-btn" onClick={handleBackfillDuplicateIdentities} disabled={submitting || loading}>
                {submitting ? 'Running…' : 'Run Backfill'}
              </button>
            </div>
            {duplicateBackfillStatus ? <div className="ops-message">{duplicateBackfillStatus}</div> : null}
          </section>

          <section className="ops-panel" style={{ marginTop: 12 }}>
            <div className="ops-nfp-table-wrap ops-support-table-wrap">
              <table className="ops-nfp-table ops-support-health-table">
                <thead>
                  <tr>
                    <th>Created</th>
                    <th>Institution</th>
                    <th>Account</th>
                    <th>Confidence</th>
                    <th>Type / Action</th>
                    <th>Owner</th>
                    <th>Duplicate / Attempted</th>
                    <th>Health</th>
                    <th>Financial Impact</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={10}>Loading duplicate account queue…</td></tr>
                  ) : duplicateDisplayRows.length === 0 ? (
                    <tr><td colSpan={10}>No duplicate Plaid account rows match the selected filters.</td></tr>
                  ) : duplicateDisplayRows.map((row) => {
                    const refs = Array.isArray(row.refs) ? row.refs : (Array.isArray(row.linkedRefs) ? row.linkedRefs : []);
                    const ownerRef = refs.find((ref) => String(ref.consumerId) === String(row.ownerConsumerId)) || refs[0] || {};
                    const duplicateRefs = refs.filter((ref) => String(ref.consumerId) !== String(row.ownerConsumerId));
                    const duplicateRef = duplicateRefs[0] || refs[1] || {};
                    const ownerConsumer = row.ownerConsumer || ownerRef.consumer;
                    const duplicateConsumer = duplicateRef.consumer;
                    return (
                      <tr key={row.id || row.key}>
                        <td>{formatDateTime(row.createdAt || row.lastDuplicateBlockedAt || row.updatedAt)}</td>
                        <td>
                          <div>{row.institutionName || '—'}</div>
                          <div className="ops-support-muted">{row.institutionId || '—'}</div>
                          {row.highRiskInstitution ? <span className="ops-chip bad">High risk</span> : null}
                        </td>
                        <td>
                          <div>{row.accountMask ? `•••• ${row.accountMask}` : '—'}</div>
                          <div className="ops-support-muted">{[row.accountType, row.accountSubtype].filter(Boolean).join(' / ') || '—'}</div>
                        </td>
                        <td><span className={`ops-chip ${(row.identityConfidence || row.matchConfidence) === 'strong' ? 'good' : 'info'}`}>{titleize(row.identityConfidence || row.matchConfidence)}</span></td>
                        <td>
                          <div><span className={`ops-chip ${row.isCrossConsumer ? 'bad' : 'info'}`}>{titleize(row.classification || row.productionClassification)}</span></div>
                          {row.productionClassification && row.productionClassification !== row.classification ? (
                            <div><span className="ops-chip">{titleize(row.productionClassification)}</span></div>
                          ) : null}
                          <div style={{ marginTop: 4 }}>
                            <span className={`ops-chip ${chipClassForAction(row.suggestedAction)}`}>
                              {titleize(row.suggestedAction || 'monitor')}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div>{formatConsumerLabel(ownerConsumer)}</div>
                          <div className="ops-support-muted">{row.ownerConsumerId || ownerRef.consumerId || '—'}</div>
                          <div className="ops-deletion-actions">{renderConsumerBadges(row.ownerConsumerType || ownerRef.consumerType)}</div>
                          <div className="ops-support-muted">Linked {formatDateTime(ownerRef.linkedAt)}</div>
                        </td>
                        <td>
                          <div>{formatConsumerLabel(duplicateConsumer)}</div>
                          <div className="ops-support-muted">{duplicateRef.consumerId || '—'}</div>
                          <div className="ops-deletion-actions">{renderConsumerBadges(duplicateRef.consumerType)}</div>
                          <div className="ops-support-muted">Attempt {formatDateTime(duplicateRef.blockedAt || duplicateRef.linkedAt)}</div>
                        </td>
                        <td>
                          <div>Owner: <span className={`ops-chip ${ownerRef.itemNeedsReauth ? 'bad' : 'good'}`}>{titleize(ownerRef.itemHealthStatus || 'unknown')}</span></div>
                          <div>Duplicate: <span className={`ops-chip ${duplicateRef.itemNeedsReauth ? 'bad' : ''}`}>{titleize(duplicateRef.itemHealthStatus || duplicateRef.status || 'unknown')}</span></div>
                          <div className="ops-support-muted">Owner sync {formatDateTime(ownerRef.itemLastSyncSuccessAt || ownerRef.lastSyncedAt)}</div>
                        </td>
                        <td>
                          <div>Pending eligible: {formatNumber(row.relatedPendingTransactions)}</div>
                          <div>Posted effects: {formatNumber(row.relatedPostedFinancialTransactions)}</div>
                        </td>
                        <td>
                          <span className={`ops-chip ${row.actionTaken === 'blocked' ? 'bad' : row.actionTaken === 'quarantined' ? 'info' : ''}`}>
                            {titleize(row.actionTaken || 'report_only')}
                          </span>
                          <div className="ops-support-muted">New item removed: {row.newItemRemoved ? 'Yes' : 'No'}</div>
                          <div className="ops-support-muted">Owner needs reauth: {row.originalOwnerNeedsReauth ? 'Yes' : 'No'}</div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : null}

      {selectedQueue === 'deletion_queue' ? (
        <>
          <section className="ops-panel ops-deletion-controls">
            <div className="ops-deletion-summary">
              <span className="ops-chip">Module: Deletion Queue</span>
              <span className="ops-chip">Pending: {summary.pending}</span>
              <span className="ops-chip">Processing: {summary.processing}</span>
              <span className="ops-chip">Role: {opsRole || 'unknown'}</span>
            </div>
            <div className="ops-deletion-filter-row">
              <label className="ops-deletion-check"><input type="checkbox" checked={statusFilters.includes('pending')} onChange={() => toggleStatus('pending')} />Pending</label>
              <label className="ops-deletion-check"><input type="checkbox" checked={statusFilters.includes('processing')} onChange={() => toggleStatus('processing')} />Processing</label>
              <input
                className="ops-deletion-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by email, username, or consumer id"
              />
              <button type="button" className="ops-btn primary" onClick={handleApplyFilters} disabled={loading}>Apply</button>
            </div>
          </section>

          <section className="ops-panel" style={{ marginTop: 12 }}>
            <div className="ops-nfp-table-wrap">
              <table className="ops-nfp-table">
                <thead>
                  <tr>
                    <th>Consumer</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Deletion Requested At</th>
                    <th>Cancelable Until</th>
                    <th>Access State</th>
                    <th>Plaid Status</th>
                    <th>Messaging Suppression</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={9}>Loading queue…</td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={9}>No consumers in selected deletion states.</td></tr>
                  ) : rows.map((row) => {
                    const isEligibleForManualFinalize = canFinalize && row.status === 'pending';
                    return (
                      <tr key={row.consumerId}>
                        <td>
                          <div>{row.consumer || row.consumerId}</div>
                          <div style={{ color: '#6f8589', fontSize: 12 }}>{row.consumerId}</div>
                        </td>
                        <td>{row.email || '—'}</td>
                        <td><span className={`ops-chip ${row.status === 'processing' ? 'bad' : ''}`}>{titleize(row.status)}</span></td>
                        <td>{formatDateTime(row.deletionRequestedAt)}</td>
                        <td>{formatDateTime(row.cancelableUntil)}</td>
                        <td><span className={`ops-chip ${row.accessState === 'disabled' ? 'bad' : ''}`}>{titleize(row.accessState)}</span></td>
                        <td>{row?.plaidStatus?.connected ? `Connected (${Number(row?.plaidStatus?.itemCount || 0)})` : 'Not connected'}</td>
                        <td>{row?.messagingSuppression?.isSuppressed ? `Suppressed (${titleize(row?.messagingSuppression?.reason || 'none')})` : 'Not suppressed'}</td>
                        <td>
                          <div className="ops-deletion-actions">
                            <button type="button" className="ops-btn" onClick={() => openDetail(row)}>View</button>
                            {isEligibleForManualFinalize ? (
                              <button
                                type="button"
                                className="ops-btn primary"
                                onClick={() => {
                                  setFinalizeRow(row);
                                  setReason(FINALIZE_REASONS[0]);
                                  setNote('');
                                  setFinalizeError('');
                                  setFinalizeSuccess('');
                                }}
                              >
                                Finalize Deletion
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : null}

      <Modal
        open={!!selectedRow}
        title={`Deletion Detail${selectedRow ? ` - ${selectedRow.consumerId}` : ''}`}
        onClose={() => { setSelectedRow(null); setDetail(null); }}
      >
        {detailLoading ? (
          <div className="ops-message">Loading detail…</div>
        ) : detail?.error ? (
          <div className="ops-message error">{detail.error}</div>
        ) : detail ? (
          <div>
            <div className="ops-kv"><div className="k">consumerId</div><div className="v">{detail.consumerId || '—'}</div></div>
            <div className="ops-kv"><div className="k">email</div><div className="v">{detail.email || '—'}</div></div>
            <div className="ops-kv"><div className="k">deletionStatus</div><div className="v">{titleize(detail.deletionStatus)}</div></div>
            <div className="ops-kv"><div className="k">deletionRequestedAt</div><div className="v">{formatDateTime(detail.deletionRequestedAt)}</div></div>
            <div className="ops-kv"><div className="k">deletionCancelableUntil</div><div className="v">{formatDateTime(detail.deletionCancelableUntil)}</div></div>
            <div className="ops-kv"><div className="k">requestSource</div><div className="v">{detail.requestSource || '—'}</div></div>
            <div className="ops-kv"><div className="k">messagingSuppressed</div><div className="v">{detail?.messagingSuppressed?.isSuppressed ? 'Yes' : 'No'}</div></div>
            <div className="ops-kv"><div className="k">Plaid connection status</div><div className="v">{detail?.plaidStatus?.connected ? `Connected (${Number(detail?.plaidStatus?.itemCount || 0)} items)` : 'Not connected'}</div></div>
            <div className="ops-kv"><div className="k">deletion job status</div><div className="v">{titleize(detail.deletionJobStatus || 'unknown')}</div></div>
          </div>
        ) : (
          <div className="ops-message">No detail available.</div>
        )}
      </Modal>

      <Modal
        open={!!finalizeRow}
        title={finalizeRow ? `Finalize Deletion - ${finalizeRow.consumerId}` : 'Finalize Deletion'}
        onClose={() => { if (!submitting) setFinalizeRow(null); }}
      >
        <div className="ops-action-form-fields">
          <label>
            Reason Code
            <select value={reason} onChange={(e) => setReason(e.target.value)}>
              {FINALIZE_REASONS.map((code) => (
                <option key={code} value={code}>{titleize(code)}</option>
              ))}
            </select>
          </label>

          <label>
            Note {reason === 'other' ? '(required)' : '(optional)'}
            <textarea
              rows={4}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add support/security context for audit trail"
            />
          </label>

          {finalizeError ? <div className="ops-message error">{finalizeError}</div> : null}
          {finalizeSuccess ? <div className="ops-message">{finalizeSuccess}</div> : null}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="ops-btn" onClick={() => setFinalizeRow(null)} disabled={submitting}>Cancel</button>
            <button type="button" className="ops-btn primary" onClick={handleFinalizeSubmit} disabled={submitting}>
              {submitting ? 'Finalizing…' : 'Confirm Finalization'}
            </button>
          </div>
        </div>
      </Modal>
    </OpsLayout>
  );
}
